// =============================================================================
// EDITEUR TEMPLATE DEVIS PDF - Design Studio
// =============================================================================
// Configuration visuelle du template de devis PDF
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Palette,
  Layout,
  Type,
  Settings,
  Save,
  RefreshCw,
  Check,
  Eye,
  ChevronDown,
  ChevronRight,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Sections configurables
const SECTIONS = [
  { id: "couleurs", label: "Couleurs", icon: Palette, description: "Palette de couleurs du document" },
  { id: "header", label: "En-tête", icon: Layout, description: "Logo, infos entreprise, RGE" },
  { id: "section_client", label: "Section Client", icon: Type, description: "Affichage des infos client" },
  { id: "section_produits", label: "Détail Prestations", icon: FileText, description: "Tableau des produits/services" },
  { id: "section_totaux", label: "Totaux & Aides", icon: Settings, description: "Récapitulatif financier" },
  { id: "mentions", label: "Mentions Légales", icon: FileText, description: "CEE, MPR, validité" },
  { id: "cgv", label: "CGV", icon: FileText, description: "Conditions générales de vente" },
  { id: "signature", label: "Zone Signature", icon: Type, description: "Zones de signature" },
  { id: "footer", label: "Pied de page", icon: Layout, description: "Pagination, infos" },
];

export default function DevisTemplateEditor({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [expandedSections, setExpandedSections] = useState(["couleurs"]);
  
  // Multi-modèles
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  }, [tenantId]);

  // Charger la liste des templates
  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        
        // Sélectionner le template par défaut ou le premier
        const defaultTemplate = data.templates?.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.id);
          loadTemplateConfig(defaultTemplate.id);
        } else if (data.templates?.length > 0) {
          setSelectedTemplateId(data.templates[0].id);
          loadTemplateConfig(data.templates[0].id);
        }
      }
    } catch (error) {
      console.error("Erreur chargement templates:", error);
      // Fallback: charger l'ancienne config
      loadConfig();
    }
  }, [getAuthHeaders]);
  
  // Charger la configuration d'un template spécifique
  const loadTemplateConfig = async (templateId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates/${templateId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.template?.config || {});
        setIsCustom(true);
      }
    } catch (error) {
      console.error("Erreur chargement config template:", error);
    } finally {
      setLoading(false);
    }
  };

  // Charger la configuration (fallback pour ancien système)
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/devis-template/config`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        setIsCustom(data.is_custom);
      }
    } catch (error) {
      console.error("Erreur chargement config:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Sauvegarder la configuration du template sélectionné
  const saveConfig = async () => {
    setSaving(true);
    try {
      let url, method;
      
      if (selectedTemplateId) {
        // Sauvegarder dans le template sélectionné
        url = `${API}/api/metier/devis-template/templates/${selectedTemplateId}`;
        method = "PUT";
      } else {
        // Fallback ancien système
        url = `${API}/api/metier/devis-template/config`;
        method = "PUT";
      }
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ config })
      });
      if (res.ok) {
        toast.success("Configuration sauvegardée");
        setIsCustom(true);
      } else {
        toast.error("Erreur de sauvegarde");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  // Créer un nouveau template
  const createTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Veuillez entrer un nom pour le modèle");
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ 
          nom: newTemplateName,
          copy_from: selectedTemplateId // Copier depuis le template actuel
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Modèle "${newTemplateName}" créé`);
        setNewTemplateName("");
        setShowNewTemplateDialog(false);
        
        // Recharger et sélectionner le nouveau template
        await loadTemplates();
        if (data.template?.id) {
          setSelectedTemplateId(data.template.id);
          await loadTemplateConfig(data.template.id);
        }
      } else {
        toast.error("Erreur création modèle");
      }
    } catch (error) {
      console.error("Erreur création:", error);
      toast.error("Erreur création modèle");
    }
  };
  
  // Définir comme modèle par défaut
  const setAsDefault = async (templateId) => {
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates/${templateId}/set-default`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success("Modèle défini par défaut");
        loadTemplates();
      }
    } catch (error) {
      console.error("Erreur:", error);
    }
  };
  
  // Supprimer un template
  const deleteTemplate = async (templateId) => {
    if (!confirm("Supprimer ce modèle ?")) return;
    
    try {
      const res = await fetch(`${API}/api/metier/devis-template/templates/${templateId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success("Modèle supprimé");
        loadTemplates();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Erreur suppression");
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
    }
  };

  // Réinitialiser aux valeurs par défaut
  const resetConfig = async () => {
    if (!confirm("Réinitialiser aux valeurs par défaut ?")) return;
    
    try {
      const res = await fetch(`${API}/api/metier/devis-template/config/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        setIsCustom(false);
        toast.success("Configuration réinitialisée");
      }
    } catch (error) {
      console.error("Erreur reset:", error);
    }
  };

  // Mettre à jour une section
  const updateSection = (sectionId, updates) => {
    setConfig(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ...updates
      }
    }));
  };

  // Toggle une section
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(s => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-zinc-400" />
          <p className="text-slate-400 mt-3">Chargement de la configuration...</p>
        </CardContent>
      </Card>
    );
  }

  if (!config) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-red-400" />
          <p className="text-slate-400 mt-3">Erreur de chargement</p>
          <Button onClick={loadConfig} className="mt-4">Réessayer</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between pb-2">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Configuration Template Devis PDF
            </CardTitle>
            <p className="text-slate-400 text-sm mt-1">
              Personnalisez l'apparence de vos devis générés
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isCustom && (
              <Button variant="outline" size="sm" onClick={resetConfig}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
            )}
            <Button onClick={saveConfig} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Sauvegarder
            </Button>
          </div>
        </CardHeader>
      </Card>
      
      {/* Sélection de modèle */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Modèles de devis</p>
              <p className="text-zinc-400 text-sm">Sélectionnez ou créez un modèle</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowNewTemplateDialog(true)}
              className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
            >
              + Nouveau modèle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => {
                  setSelectedTemplateId(template.id);
                  loadTemplateConfig(template.id);
                }}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedTemplateId === template.id
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-slate-600 hover:border-slate-500 bg-slate-700/30"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-medium text-sm">{template.nom}</p>
                    {template.is_default && (
                      <span className="text-xs text-green-400">Par défaut</span>
                    )}
                  </div>
                  {selectedTemplateId === template.id && (
                    <Check className="w-4 h-4 text-blue-400" />
                  )}
                </div>
                {!template.is_default && selectedTemplateId === template.id && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-slate-600">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs h-6 text-green-400 hover:bg-green-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAsDefault(template.id);
                      }}
                    >
                      Définir par défaut
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs h-6 text-red-400 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template.id);
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Dialog nouveau modèle */}
          {showNewTemplateDialog && (
            <div className="mt-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
              <p className="text-white font-medium mb-3">Créer un nouveau modèle</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Nom du modèle..."
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="flex-1 bg-slate-800 border-slate-600 text-white"
                />
                <Button onClick={createTemplate} className="bg-blue-600 hover:bg-blue-700">
                  Créer
                </Button>
                <Button variant="ghost" onClick={() => setShowNewTemplateDialog(false)}>
                  Annuler
                </Button>
              </div>
              <p className="text-zinc-400 text-xs mt-2">
                Le nouveau modèle sera une copie du modèle actuellement sélectionné
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sections configurables */}
      <div className="space-y-3">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          const sectionConfig = config[section.id] || {};
          
          return (
            <Collapsible key={section.id} open={isExpanded} onOpenChange={() => toggleSection(section.id)}>
              <Card className="bg-slate-800/50 border-slate-700">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between py-3 cursor-pointer hover:bg-slate-700/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-slate-700/50">
                        <Icon className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{section.label}</p>
                        <p className="text-zinc-400 text-xs">{section.description}</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-4 border-t border-slate-700">
                    {/* Contenu spécifique à chaque section */}
                    {section.id === "couleurs" && (
                      <CouleursSection config={sectionConfig} onChange={(u) => updateSection("couleurs", u)} />
                    )}
                    {section.id === "header" && (
                      <HeaderSection config={sectionConfig} onChange={(u) => updateSection("header", u)} />
                    )}
                    {section.id === "section_client" && (
                      <ClientSection config={sectionConfig} onChange={(u) => updateSection("section_client", u)} />
                    )}
                    {section.id === "section_produits" && (
                      <ProduitsSection config={sectionConfig} onChange={(u) => updateSection("section_produits", u)} />
                    )}
                    {section.id === "section_totaux" && (
                      <TotauxSection config={sectionConfig} onChange={(u) => updateSection("section_totaux", u)} />
                    )}
                    {section.id === "mentions" && (
                      <MentionsSection config={sectionConfig} onChange={(u) => updateSection("mentions", u)} />
                    )}
                    {section.id === "cgv" && (
                      <CGVSection config={sectionConfig} onChange={(u) => updateSection("cgv", u)} />
                    )}
                    {section.id === "signature" && (
                      <SignatureSection config={sectionConfig} onChange={(u) => updateSection("signature", u)} />
                    )}
                    {section.id === "footer" && (
                      <FooterSection config={sectionConfig} onChange={(u) => updateSection("footer", u)} />
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}

// === SOUS-COMPOSANTS POUR CHAQUE SECTION ===

function CouleursSection({ config, onChange }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
      <div>
        <Label className="text-slate-300 text-xs">Couleur primaire</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={config.primaire || "#FF6B00"}
            onChange={(e) => onChange({ primaire: e.target.value })}
            className="w-10 h-8 rounded cursor-pointer"
          />
          <Input
            value={config.primaire || "#FF6B00"}
            onChange={(e) => onChange({ primaire: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">Couleur secondaire</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={config.secondaire || "#1E40AF"}
            onChange={(e) => onChange({ secondaire: e.target.value })}
            className="w-10 h-8 rounded cursor-pointer"
          />
          <Input
            value={config.secondaire || "#1E40AF"}
            onChange={(e) => onChange({ secondaire: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">Texte principal</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={config.texte || "#111827"}
            onChange={(e) => onChange({ texte: e.target.value })}
            className="w-10 h-8 rounded cursor-pointer"
          />
          <Input
            value={config.texte || "#111827"}
            onChange={(e) => onChange({ texte: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">Aides/Réductions</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={config.aide_positive || "#059669"}
            onChange={(e) => onChange({ aide_positive: e.target.value })}
            className="w-10 h-8 rounded cursor-pointer"
          />
          <Input
            value={config.aide_positive || "#059669"}
            onChange={(e) => onChange({ aide_positive: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white text-xs h-8"
          />
        </div>
      </div>
    </div>
  );
}

function HeaderSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher logo entreprise</Label>
          <Switch
            checked={config.afficher_logo !== false}
            onCheckedChange={(v) => onChange({ afficher_logo: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher logo(s) RGE</Label>
          <Switch
            checked={config.afficher_logo_rge !== false}
            onCheckedChange={(v) => onChange({ afficher_logo_rge: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher SIRET</Label>
          <Switch
            checked={config.afficher_siret !== false}
            onCheckedChange={(v) => onChange({ afficher_siret: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher TVA Intra</Label>
          <Switch
            checked={config.afficher_tva_intra !== false}
            onCheckedChange={(v) => onChange({ afficher_tva_intra: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher assurance</Label>
          <Switch
            checked={config.afficher_assurance !== false}
            onCheckedChange={(v) => onChange({ afficher_assurance: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher téléphone</Label>
          <Switch
            checked={config.afficher_telephone !== false}
            onCheckedChange={(v) => onChange({ afficher_telephone: v })}
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-sm">Position du logo</Label>
        <Select
          value={config.position_logo || "gauche"}
          onValueChange={(v) => onChange({ position_logo: v })}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gauche">Gauche</SelectItem>
            <SelectItem value="centre">Centre</SelectItem>
            <SelectItem value="droite">Droite</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ClientSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      {/* Mention adresse identique */}
      <div>
        <Label className="text-slate-300 text-sm">Mention si adresse travaux identique</Label>
        <Input
          value={config.mention_adresse_identique || "Idem adresse client"}
          onChange={(e) => onChange({ mention_adresse_identique: e.target.value })}
          className="bg-slate-800 border-slate-700 text-white mt-1"
          placeholder="Idem adresse client"
        />
      </div>
      
      {/* Switches pour les options d'affichage */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher adresse travaux</Label>
          <Switch
            checked={config.afficher_zone_travaux !== false}
            onCheckedChange={(v) => onChange({ afficher_zone_travaux: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher profil fiscal</Label>
          <Switch
            checked={config.afficher_profil_fiscal !== false}
            onCheckedChange={(v) => onChange({ afficher_profil_fiscal: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher zone climatique</Label>
          <Switch
            checked={config.afficher_zone_climatique !== false}
            onCheckedChange={(v) => onChange({ afficher_zone_climatique: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher type de logement</Label>
          <Switch
            checked={config.afficher_type_logement !== false}
            onCheckedChange={(v) => onChange({ afficher_type_logement: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher âge du logement</Label>
          <Switch
            checked={config.afficher_age_logement !== false}
            onCheckedChange={(v) => onChange({ afficher_age_logement: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher surface (m²)</Label>
          <Switch
            checked={config.afficher_surface !== false}
            onCheckedChange={(v) => onChange({ afficher_surface: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher chauffage actuel</Label>
          <Switch
            checked={config.afficher_chauffage_actuel !== false}
            onCheckedChange={(v) => onChange({ afficher_chauffage_actuel: v })}
          />
        </div>
      </div>
    </div>
  );
}

function ProduitsSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-slate-300 text-sm">Style du tableau</Label>
        <Select
          value={config.style_tableau || "alterne"}
          onValueChange={(v) => onChange({ style_tableau: v })}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">Simple</SelectItem>
            <SelectItem value="alterne">Lignes alternées</SelectItem>
            <SelectItem value="bordure">Avec bordures</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher quantité</Label>
          <Switch
            checked={config.afficher_quantite !== false}
            onCheckedChange={(v) => onChange({ afficher_quantite: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher prix unitaire</Label>
          <Switch
            checked={config.afficher_prix_unitaire !== false}
            onCheckedChange={(v) => onChange({ afficher_prix_unitaire: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher marque/modèle</Label>
          <Switch
            checked={config.afficher_marque_modele !== false}
            onCheckedChange={(v) => onChange({ afficher_marque_modele: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher fiche CEE</Label>
          <Switch
            checked={config.afficher_fiche_cee !== false}
            onCheckedChange={(v) => onChange({ afficher_fiche_cee: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher COP/SCOP</Label>
          <Switch
            checked={config.afficher_cop_scop !== false}
            onCheckedChange={(v) => onChange({ afficher_cop_scop: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher ETAS</Label>
          <Switch
            checked={config.afficher_etas !== false}
            onCheckedChange={(v) => onChange({ afficher_etas: v })}
          />
        </div>
      </div>
      
      {/* Installateur par produit */}
      <div className="border-t border-slate-700 pt-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-slate-300 text-sm">Mentions installateur par produit</Label>
            <p className="text-zinc-400 text-xs">Affiche les qualifications RGE sous chaque produit éligible CEE</p>
          </div>
          <Switch
            checked={config.afficher_installateur_par_produit !== false}
            onCheckedChange={(v) => onChange({ afficher_installateur_par_produit: v })}
          />
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Template mention installateur</Label>
          <Input
            value={config.mention_installateur_template || "Matériel(s) fourni(s) et mis en place par {raison_sociale}, SIRET {siret}"}
            onChange={(e) => onChange({ mention_installateur_template: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white mt-1"
            placeholder="Matériel(s) fourni(s) et mis en place par {raison_sociale}, SIRET {siret}"
          />
          <p className="text-zinc-400 text-xs mt-1">Variables: {"{raison_sociale}"}, {"{siret}"}</p>
        </div>
      </div>
    </div>
  );
}

function TotauxSection({ config, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Afficher total HT</Label>
        <Switch
          checked={config.afficher_total_ht !== false}
          onCheckedChange={(v) => onChange({ afficher_total_ht: v })}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Afficher détail TVA</Label>
        <Switch
          checked={config.afficher_detail_tva !== false}
          onCheckedChange={(v) => onChange({ afficher_detail_tva: v })}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Séparer aides CEE/MPR</Label>
        <Switch
          checked={config.afficher_aides_separees !== false}
          onCheckedChange={(v) => onChange({ afficher_aides_separees: v })}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Mettre en avant le RAC</Label>
        <Switch
          checked={config.mettre_en_avant_rac !== false}
          onCheckedChange={(v) => onChange({ mettre_en_avant_rac: v })}
        />
      </div>
    </div>
  );
}

function MentionsSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Mentions CEE</Label>
          <Switch
            checked={config.afficher_mentions_cee !== false}
            onCheckedChange={(v) => onChange({ afficher_mentions_cee: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Mentions MPR</Label>
          <Switch
            checked={config.afficher_mentions_mpr !== false}
            onCheckedChange={(v) => onChange({ afficher_mentions_mpr: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher validité</Label>
          <Switch
            checked={config.afficher_validite !== false}
            onCheckedChange={(v) => onChange({ afficher_validite: v })}
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-sm">Durée de validité (jours)</Label>
        <Input
          type="number"
          min={7}
          max={90}
          value={config.duree_validite_jours || 30}
          onChange={(e) => onChange({ duree_validite_jours: parseInt(e.target.value) })}
          className="bg-slate-800 border-slate-700 text-white mt-1 w-32"
        />
      </div>
    </div>
  );
}

function CGVSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-slate-300 text-sm">Position des CGV</Label>
        <Select
          value={config.position || "apres_signature"}
          onValueChange={(v) => onChange({ position: v })}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="avant_signature">Avant la signature</SelectItem>
            <SelectItem value="apres_signature">Après la signature</SelectItem>
            <SelectItem value="page_separee">Page séparée</SelectItem>
            <SelectItem value="lien_externe">Lien externe uniquement</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-slate-300 text-sm">Format d'affichage</Label>
        <Select
          value={config.format_affichage || "mention_simple"}
          onValueChange={(v) => onChange({ format_affichage: v })}
        >
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bloc_complet">Bloc complet</SelectItem>
            <SelectItem value="resume_avec_lien">Résumé avec lien</SelectItem>
            <SelectItem value="mention_simple">Mention simple</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function SignatureSection({ config, onChange }) {
  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Zone prestataire</Label>
          <Switch
            checked={config.afficher_zone_prestataire !== false}
            onCheckedChange={(v) => onChange({ afficher_zone_prestataire: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Zone client</Label>
          <Switch
            checked={config.afficher_zone_client !== false}
            onCheckedChange={(v) => onChange({ afficher_zone_client: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Afficher date</Label>
          <Switch
            checked={config.afficher_date !== false}
            onCheckedChange={(v) => onChange({ afficher_date: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">Mode de paiement</Label>
          <Switch
            checked={config.afficher_mode_paiement !== false}
            onCheckedChange={(v) => onChange({ afficher_mode_paiement: v })}
          />
        </div>
      </div>
    </div>
  );
}

function FooterSection({ config, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Pagination</Label>
        <Switch
          checked={config.afficher_pagination !== false}
          onCheckedChange={(v) => onChange({ afficher_pagination: v })}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Infos société</Label>
        <Switch
          checked={config.afficher_infos_societe !== false}
          onCheckedChange={(v) => onChange({ afficher_infos_societe: v })}
        />
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 text-sm">Mentions RGE</Label>
        <Switch
          checked={config.afficher_mentions_rge !== false}
          onCheckedChange={(v) => onChange({ afficher_mentions_rge: v })}
        />
      </div>
    </div>
  );
}
